// i18n runtime translations using ttag.js
import React from 'react'
import { t, addLocale, useLocale as activateLocale } from 'ttag'

// ttag generated translation json files
import nb from 'i18n/nb.json'
import en from 'i18n/en.json'

// import for side effects
import moment from 'moment'
import 'moment/locale/en-gb'
import 'moment/locale/nb'

// Language codes in use
const LC = { ENGLISH: 'en', NORWEGIAN: 'nb' }
type TtagLocale = Parameters<typeof addLocale>[1]

const ttagLocales: Record<string, TtagLocale> = {
  [LC.ENGLISH]: en as unknown as TtagLocale,
  [LC.NORWEGIAN]: nb as unknown as TtagLocale,
}

export const browserLanguage = () => {
  try {
    const language = navigator.languages
      .map(lc => lc.slice(0, 2)) // two letter language code
      .find(lc => Object.hasOwn(ttagLocales, lc))
    return language ?? LC.ENGLISH
  } catch (err) {
    console.error('cannot determine browser language', err)
    return LC.ENGLISH
  }
}

export const languageName = lc => {
  const names = {
    [LC.NORWEGIAN]: t`Norwegian`,
    [LC.ENGLISH]: t`English`,
  }
  return names[lc] || lc
}

// get select options for candidate status
// label corresponds to the tab the candidate will be moved to
export const statusSelectOptions = ({
  hasInterviewModule,
}: { hasInterviewModule?: boolean } = {}) => [
  { value: 'created', label: t`Send invites` },
  { value: 'completed', label: t`Test scores` },
  ...(hasInterviewModule
    ? [
        { value: 'screened', label: t`Interviews` },
        { value: 'interviewed', label: t`Final results` },
      ]
    : []),
]

// Get options for available languages for use with <select /> form elements
export const languageSelectOptions = (languages?: string[] | null) =>
  (languages ?? Object.keys(ttagLocales)).map(lc => ({
    value: lc,
    label: languageName(lc),
  }))

const setGlobalLanguage = lc => {
  // sets new translation language for the entire application
  // using ttags for text i18n and moment.js for time and date l10n
  const ttagLocale = ttagLocales[lc]
  if (!ttagLocale) throw new Error(`language ${lc} is missing`)
  // ttags activate translations for language code: lc
  addLocale(lc, ttagLocale)
  activateLocale(lc)
  // moment.js activate translations for language code: lc
  moment.locale(lc)
}

const TranslationContext = React.createContext<string | undefined>(LC.ENGLISH)

// useTranslation: language_code => language_code
export const useTranslation = language => {
  // React hook for setting runtime ttags translation
  // React.useState will force the component rerender after language change
  if (!language) language = browserLanguage()
  // activated on the first render too, so the Translate key does not change
  // right after mount and remount the whole tree
  const [currentLanguage, setLocalLanguage] = React.useState<string>(() => {
    setGlobalLanguage(language)
    return language
  })
  React.useEffect(() => {
    setGlobalLanguage(language)
    setLocalLanguage(language)
  }, [language])
  return currentLanguage
}

// useLanguage: () => language_code
// -> language code from current translation context Provider
export const useLanguage = () => React.useContext(TranslationContext)

export const Translate = props => {
  // higher order component to wrap apps for i18n translations
  // enables use of ttag template strings at runtime
  //
  // also enables global moment.js localization
  //
  // Moment and Ttags makes use of their own global state objects outside of
  // react, so we have to force a rerender to make sure all translation strings
  // are refreshed if the language is changed at runtime with no hard reload of
  // the entire web page
  const language = useTranslation(props.language)
  return (
    <TranslationContext.Provider
      // TODO: it should be removed since it forces unmount/mount
      // tree once again which shouldn't be necessary, remove it
      // and fix the translation issues
      key={language} // force rerender to make ttags translation active
      value={language}
    >
      {props.children}
    </TranslationContext.Provider>
  )
}
